import { Box, Button, Radio, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGES, type LanguageCode } from "~/utils/languages";
import { CenterModal } from "../CenterModal";

interface ChangeLanguageModalProps {
  open: boolean;
  onClose: () => void;
  currentLanguage: LanguageCode;
  onSave: (language: LanguageCode) => void;
}

export function ChangeLanguageModal({
  open,
  onClose,
  currentLanguage,
  onSave,
}: ChangeLanguageModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedLang, setSelectedLang] = useState<LanguageCode>(currentLanguage);

  useEffect(() => {
    if (open) setSelectedLang(currentLanguage);
  }, [open, currentLanguage]);

  return (
    <CenterModal
      open={open}
      onClose={onClose}
      title={t("auth.changeLanguage")}
      width={{ xs: "calc(100% - 32px)", md: 480 }}
      paperSx={{
        bgcolor: theme.palette.custom.gray[0],
        border: `1px solid ${theme.palette.custom.gray[200]}`,
        borderRadius: "var(--Card-Radius-Medium)",
        boxShadow: "0 2px 40px rgba(0,0,0,0.1)",
        py: 1,
      }}
      headerSx={{ px: 3, pt: 1.5, pb: 1.5 }}
      titleSx={{
        fontWeight: 700,
        fontSize: 32,
        lineHeight: 1.33,
        letterSpacing: "-0.32px",
        color: theme.palette.custom.gray[800],
      }}
      closeButtonSx={{
        mt: 0.5,
        color: theme.palette.custom.gray[700],
        mr: 0,
      }}
      contentSx={{ px: 0, pt: 0, pb: 0 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 3,
          py: 1.5,
        }}
      >
        {LANGUAGES.map((lang) => {
          const isSelected = selectedLang === lang.code;
          return (
            <Box
              key={lang.code}
              onClick={() => setSelectedLang(lang.code)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                minHeight: 52,
                px: 2,
                py: 1.5,
                border: "1px solid",
                borderColor: isSelected
                  ? theme.palette.custom.primary
                  : theme.palette.custom.gray[300],
                borderRadius: "var(--Input-Radius-Medium)",
                bgcolor: isSelected
                  ? theme.palette.custom.primarySecondaryPressed
                  : theme.palette.custom.gray[0],
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <Radio
                checked={isSelected}
                sx={{
                  p: 0,
                  color: theme.palette.custom.gray[300],
                  "&.Mui-checked": { color: theme.palette.custom.primary },
                }}
              />
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: theme.palette.custom.gray[700],
                }}
              >
                {lang.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => onSave(selectedLang)}
          sx={{
            minHeight: 52,
            borderRadius: "var(--Input-Radius-Medium)",
            bgcolor: theme.palette.custom.primary,
            fontWeight: 600,
            fontSize: 16,
            textTransform: "none",
            "&:hover": { bgcolor: theme.palette.custom.primaryHover },
          }}
        >
          {t("auth.saveChange")}
        </Button>
      </Box>
    </CenterModal>
  );
}
